import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Scale, Plus, TrendingDown, TrendingUp, Target, Calendar, Edit, Save, X, Trash2 } from 'lucide-react';
import { WeightEntry, DatabaseSchema, GoalProgress } from '../types';
import { format } from 'date-fns';
import { WeekNavigation } from './WeekNavigation';
import { getWeekDays } from '../utils/weekUtils';
import { useToast } from './shared/ToastProvider';

interface WeightTrackerProps {
  database: DatabaseSchema | null;
  onAddWeight: (weight: WeightEntry) => Promise<void>;
  onUpdateGoals: (goals: GoalProgress) => Promise<void>;
  onDeleteWeight?: (id: string) => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`weight-tabpanel-${index}`}
      aria-labelledby={`weight-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const WeightTracker: React.FC<WeightTrackerProps> = ({ database, onAddWeight, onUpdateGoals, onDeleteWeight }) => {
  const toast = useToast();
  const [weight, setWeight] = useState('');
  const [bodyFatPercentage, setBodyFatPercentage] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [editWeight, setEditWeight] = useState('');
  const [editBodyFat, setEditBodyFat] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  
  // Goals form state
  const [showGoalsForm, setShowGoalsForm] = useState(false);
  const [initialWeight, setInitialWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');

  const weights = database?.weights || [];
  const sortedWeights = [...weights].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Initialize goals form with preferences
  useEffect(() => {
    if (database?.preferences?.initialWeight && database?.preferences?.goalWeight) {
      setInitialWeight(database.preferences.initialWeight.toString());
      setTargetWeight(database.preferences.goalWeight.toString());
    }
  }, [database?.preferences]);

  // Check for duplicates (logging only, no automatic cleanup)
  useEffect(() => {
    if (weights.length > 0) {
      const dateCount = new Map<string, number>();
      weights.forEach(weight => {
        dateCount.set(weight.date, (dateCount.get(weight.date) || 0) + 1);
      });
      
      const hasDuplicates = Array.from(dateCount.values()).some(count => count > 1);
      
      if (hasDuplicates) {
        console.log('Duplicate weight entries detected for some dates');
      }
    }
  }, [weights]);
  
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  const handleWeekChange = (week: Date) => {
    setSelectedWeek(week);
  };
  
  const getWeeklyWeights = () => {
    const weekDays = getWeekDays(selectedWeek);
    return weekDays.map(day => {
      const dayWeights = weights.filter(w => w.date === day.dateString);
      const latestWeight = dayWeights.sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      )[0];
      return {
        ...day,
        weight: latestWeight,
        hasWeight: !!latestWeight
      };
    });
  };
  
  const weeklyData = getWeeklyWeights();
  
  const handleEditDay = (day: any) => {
    setEditingDay(day.dateString);
    if (day.weight) {
      setEditWeight(day.weight.weight.toString());
      setEditBodyFat(day.weight.bodyFatPercentage?.toString() || '');
      setEditNotes(day.weight.notes || '');
    } else {
      setEditWeight('');
      setEditBodyFat('');
      setEditNotes('');
    }
  };
  
  const handleSaveEdit = async () => {
    if (!editWeight || !editingDay) return;
    
    // Validate the date is valid
    const dateObj = new Date(editingDay);
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', editingDay);
      return;
    }
    
    setQuickAddLoading(true);
    
    try {
      const height = database?.preferences?.height || 169;
      const weightNum = parseFloat(editWeight);
      const bmi = weightNum / Math.pow(height / 100, 2);
      
      // Check if a weight entry already exists for this date
      const existingWeight = weights.find(w => w.date === editingDay);
      
      const weightEntry: WeightEntry = {
        id: existingWeight?.id || `weight_${Date.now()}`,
        date: editingDay,
        weight: weightNum,
        height,
        bodyFatPercentage: editBodyFat ? parseFloat(editBodyFat) : undefined,
        bmi,
        notes: editNotes || undefined,
      };

      await onAddWeight(weightEntry);
      toast.success('Weight entry saved!');
      setEditingDay(null);
      setEditWeight('');
      setEditBodyFat('');
      setEditNotes('');
    } catch (error) {
      console.error('Error saving weight:', error);
      toast.error('Failed to save weight entry.');
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleCancelEdit = () => {
    setEditingDay(null);
    setEditWeight('');
    setEditBodyFat('');
    setEditNotes('');
  };

  const handleDeleteWeight = async (weightEntry: WeightEntry) => {
    if (!onDeleteWeight) return;
    
    const confirmDelete = window.confirm(`Delete weight entry from ${format(new Date(weightEntry.date), 'MMM dd, yyyy')}?`);
    if (confirmDelete) {
      await onDeleteWeight(weightEntry.id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!weight) return;

    setLoading(true);
    
    try {
      const userHeight = database?.preferences?.height || 169; // Default to 169cm (1.69m)
      
      const newEntry: WeightEntry = {
        id: `weight_${Date.now()}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        weight: parseFloat(weight),
        height: userHeight,
        bodyFatPercentage: bodyFatPercentage ? parseFloat(bodyFatPercentage) : undefined,
        notes: notes || undefined,
      };

      await onAddWeight(newEntry);
      toast.success('Weight entry added successfully!');

      setWeight('');
      setBodyFatPercentage('');
      setNotes('');
    } catch (error) {
      console.error('Error adding weight:', error);
      toast.error('Failed to add weight entry.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoalsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const startDate = database?.preferences?.startDate;
    if (!initialWeight || !targetWeight || !startDate) return;

    setLoading(true);
    
    try {
      // Get monthly target from preferences or use default
      const monthlyTarget = database?.preferences?.monthlyWeightLossTarget || 4.0;
      
      // Calculate total weight to lose
      const totalWeightToLose = parseFloat(initialWeight) - parseFloat(targetWeight);
      
      // Calculate months needed based on monthly target
      const monthsNeeded = totalWeightToLose / monthlyTarget;
      
      // Calculate target date by adding months to start date
      const startDateObj = new Date(startDate!);
      const calculatedTargetDate = new Date(startDateObj);
      calculatedTargetDate.setMonth(calculatedTargetDate.getMonth() + Math.ceil(monthsNeeded));
      
      // Calculate weekly target from monthly target
      const calculatedWeeklyTarget = monthlyTarget / 4.33; // Average weeks per month
      
      const goals: GoalProgress = {
        initialWeight: parseFloat(initialWeight),
        startWeight: parseFloat(initialWeight),
        currentWeight: parseFloat(initialWeight),
        targetWeight: parseFloat(targetWeight),
        startDate: startDate!,
        targetDate: format(calculatedTargetDate, 'yyyy-MM-dd'),
        weeklyTarget: parseFloat(calculatedWeeklyTarget.toFixed(2))
      };

      await onUpdateGoals(goals);
      toast.success('Weight loss goals updated!');
      setShowGoalsForm(false);
    } catch (error) {
      console.error('Error updating goals:', error);
      toast.error('Failed to update goals.');
    } finally {
      setLoading(false);
    }
  };

  const calculateBMI = (weight: number, height?: number) => {
    if (!height) return null;
    const heightInM = height / 100;
    return weight / (heightInM * heightInM);
  };

  const getWeightChange = (current: WeightEntry, previous?: WeightEntry) => {
    if (!previous) return null;
    return current.weight - previous.weight;
  };

  const getBMICategory = (bmi: number) => {
    if (bmi < 18.5) return { label: 'Underweight', color: 'info' };
    if (bmi < 25) return { label: 'Normal', color: 'success' };
    if (bmi < 30) return { label: 'Overweight', color: 'warning' };
    return { label: 'Obese', color: 'error' };
  };

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Weight Tracking
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab 
            label="Quick Entry" 
            icon={<Plus size={20} />} 
            iconPosition="start"
          />
          <Tab 
            label="Weekly View" 
            icon={<Calendar size={20} />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0}>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Plus size={24} color="#6366f1" />
                <Typography variant="h6">Add Weight Entry</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  inputProps={{ step: "0.1", min: "0" }}
                />
                
                <TextField
                  fullWidth
                  label="Body Fat Percentage (%)"
                  type="number"
                  value={bodyFatPercentage}
                  onChange={(e) => setBodyFatPercentage(e.target.value)}
                  sx={{ mb: 2 }}
                  inputProps={{ step: "0.1", min: "0", max: "100" }}
                />
                
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ mb: 3 }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={!weight || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Adding...' : 'Add Weight Entry'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {!database?.goals && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Set up your weight loss goals to track progress</Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setShowGoalsForm(true)}
                >
                  Set Goals
                </Button>
              </Box>
            </Alert>
          )}

          {showGoalsForm && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Target size={24} color="#10b981" />
                  <Typography variant="h6">Set Weight Loss Goals</Typography>
                </Box>

                <Box component="form" onSubmit={handleGoalsSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Current Weight (kg)"
                        type="number"
                        value={initialWeight}
                        onChange={(e) => setInitialWeight(e.target.value)}
                        required
                        inputProps={{ step: "0.1", min: "0" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Target Weight (kg)"
                        type="number"
                        value={targetWeight}
                        onChange={(e) => setTargetWeight(e.target.value)}
                        required
                        inputProps={{ step: "0.1", min: "0" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {database?.preferences?.startDate ? 
                            format(new Date(database.preferences.startDate), 'MMMM d, yyyy') : 
                            'Set in Settings'
                          }
                        </Typography>
                        {!database?.preferences?.startDate && (
                          <Typography variant="caption" color="text.secondary">
                            Configure start date in Settings tab
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Estimated Completion
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {initialWeight && targetWeight && database?.preferences?.startDate ? (() => {
                            const monthlyTarget = database?.preferences?.monthlyWeightLossTarget || 4.0;
                            const totalToLose = parseFloat(initialWeight) - parseFloat(targetWeight);
                            const monthsNeeded = totalToLose / monthlyTarget;
                            const targetDateCalc = new Date(database.preferences.startDate);
                            targetDateCalc.setMonth(targetDateCalc.getMonth() + Math.ceil(monthsNeeded));
                            return format(targetDateCalc, 'MMMM yyyy');
                          })() : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ~{initialWeight && targetWeight ? 
                            Math.ceil((parseFloat(initialWeight) - parseFloat(targetWeight)) / (database?.preferences?.monthlyWeightLossTarget || 4.0))
                          : 0} months
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.main">
                          <strong>Monthly Target:</strong> {database?.preferences?.monthlyWeightLossTarget || 4.0} kg/month
                          ({((database?.preferences?.monthlyWeightLossTarget || 4.0) / 4.33).toFixed(2)} kg/week)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Based on your preferences settings. Recommended: 2-4 kg per month for sustainable weight loss.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box display="flex" gap={2} mt={3}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!initialWeight || !targetWeight || !database?.preferences?.startDate || loading}
                      sx={{ flexGrow: 1 }}
                    >
                      {loading ? 'Setting Goals...' : 'Set Goals'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowGoalsForm(false)}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {sortedWeights.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Stats
                </Typography>
                
                <Box display="flex" gap={2} mb={2}>
                  <Chip 
                    icon={<Scale />} 
                    label={`${sortedWeights[0].weight} kg`}
                    color="primary"
                    variant="outlined"
                  />
                  
                  {sortedWeights[0].height && (
                    <Chip 
                      label={`BMI: ${calculateBMI(sortedWeights[0].weight, sortedWeights[0].height)?.toFixed(1)}`}
                      color={getBMICategory(calculateBMI(sortedWeights[0].weight, sortedWeights[0].height)!).color as any}
                      variant="outlined"
                    />
                  )}
                  
                  {sortedWeights[0].bodyFatPercentage && (
                    <Chip 
                      label={`${sortedWeights[0].bodyFatPercentage}% BF`}
                      variant="outlined"
                    />
                  )}
                </Box>

                {sortedWeights.length > 1 && (
                  <Box>
                    {(() => {
                      const change = getWeightChange(sortedWeights[0], sortedWeights[1]);
                      if (!change) return null;
                      
                      return (
                        <Alert 
                          severity={change < 0 ? 'success' : change > 0 ? 'warning' : 'info'}
                          icon={change < 0 ? <TrendingDown /> : <TrendingUp />}
                        >
                          {change > 0 ? '+' : ''}{change.toFixed(1)} kg since last entry
                        </Alert>
                      );
                    })()}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weight History
              </Typography>
              
              {sortedWeights.length === 0 ? (
                <Typography color="text.secondary">
                  No weight entries yet. Add your first weigh-in!
                </Typography>
              ) : (
                <List>
                  {sortedWeights.map((entry, index) => {
                    const previous = sortedWeights[index + 1];
                    const change = getWeightChange(entry, previous);
                    const bmi = entry.height ? calculateBMI(entry.weight, entry.height) : null;
                    
                    return (
                      <React.Fragment key={entry.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography fontWeight="bold">
                                  {entry.weight} kg
                                </Typography>
                                {change !== null && (
                                  <Chip 
                                    size="small"
                                    label={`${change > 0 ? '+' : ''}${change.toFixed(1)}`}
                                    color={change < 0 ? 'success' : change > 0 ? 'error' : 'default'}
                                  />
                                )}
                                {bmi && (
                                  <Chip 
                                    size="small"
                                    label={`BMI ${bmi.toFixed(1)}`}
                                    color={getBMICategory(bmi).color as any}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {format(new Date(entry.date), 'MMM dd, yyyy')}
                                </Typography>
                                {entry.bodyFatPercentage && (
                                  <Typography variant="body2" color="text.secondary">
                                    Body Fat: {entry.bodyFatPercentage}%
                                  </Typography>
                                )}
                                {entry.notes && (
                                  <Typography variant="body2" color="text.secondary">
                                    {entry.notes}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < sortedWeights.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <WeekNavigation 
          currentWeek={selectedWeek}
          onWeekChange={handleWeekChange}
          title="Weekly Weight Overview"
        />
        
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Day</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Weight (kg)</strong></TableCell>
                    <TableCell align="right"><strong>BMI</strong></TableCell>
                    <TableCell align="right"><strong>Body Fat %</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyData.map((day) => (
                    <TableRow 
                      key={day.dateString}
                      sx={{ 
                        backgroundColor: day.isToday ? 'primary.main' : 'transparent',
                        '&:hover': { backgroundColor: day.isToday ? 'primary.main' : '#f1f5f9' }
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography 
                            variant="body2" 
                            fontWeight={day.isToday ? 'bold' : 'normal'}
                            color={day.isToday ? 'white' : 'text.primary'}
                          >
                            {day.dayShort}
                          </Typography>
                          {day.isToday && (
                            <Chip size="small" label="Today" color="primary" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2"
                          color={day.isToday ? 'white' : 'text.secondary'}
                        >
                          {format(day.date, 'MMM dd')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {day.weight ? (
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={day.isToday ? 'white' : 'text.primary'}
                          >
                            {day.weight.weight} kg
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            color="text.disabled"
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {day.weight?.bmi ? (
                          <Chip 
                            size="small" 
                            label={day.weight.bmi.toFixed(1)} 
                            color={getBMICategory(day.weight.bmi).color as any}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {day.weight?.bodyFatPercentage ? (
                          <Typography variant="body2">
                            {day.weight.bodyFatPercentage}%
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {day.weight?.notes ? (
                          <Typography variant="body2" fontSize={0.8}>
                            {day.weight.notes}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditDay(day)}
                            color={day.weight ? "primary" : "success"}
                          >
                            {day.weight ? <Edit size={16} /> : <Plus size={16} />}
                          </IconButton>
                          {day.weight && onDeleteWeight && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteWeight(day.weight!)}
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>
      
      {/* Edit Weight Dialog */}
      <Dialog open={!!editingDay} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDay && (
            weeklyData.find(d => d.dateString === editingDay)?.weight 
              ? `Edit Weight - ${format(new Date(editingDay), 'EEEE, MMM dd')}` 
              : `Add Weight - ${format(new Date(editingDay), 'EEEE, MMM dd')}`
          )}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Weight (kg)"
              type="number"
              value={editWeight}
              onChange={(e) => setEditWeight(e.target.value)}
              required
              sx={{ mb: 2 }}
              inputProps={{ step: "0.1", min: "0" }}
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Body Fat Percentage (%)"
              type="number"
              value={editBodyFat}
              onChange={(e) => setEditBodyFat(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ step: "0.1", min: "0", max: "100" }}
            />
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<X size={16} />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={!editWeight || quickAddLoading}
            startIcon={<Save size={16} />}
          >
            {quickAddLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};